import { ChangeEvent, PropsWithChildren } from "react";
import { Persona } from "@/api/personas";
import { EditablePersonaHeader } from "@/components/manage-personas/EditablePersonaHeader.tsx";
import { apiUrl, PersonaDocuments } from "@/api/api.ts";
import { PersonaKnowledgeBaseStatus } from "@/components/manage-personas/PersonaKnowledgeBaseStatus.tsx";
import { Role } from "@/components/AuthorizationProvider.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Input } from "@/components/ui/input.tsx";
import { TwitterConnectionStatus } from "@/components/manage-personas/TwitterConnectionStatus.tsx";
import { useAuth } from "@/hooks/use-auth.tsx";

export function PersonaDetailsForm({
  persona: personaData,
  documents,
  onFileChange,
  onSave,
  children,
  showHeaderOptions,
}: PropsWithChildren<{
  persona: Persona;
  documents: PersonaDocuments;
  onFileChange: (
    personaId: string,
    event: ChangeEvent<HTMLInputElement>,
  ) => Promise<string>;
  onSave: (persona: Persona, documents: PersonaDocuments) => Promise<void>;
  showHeaderOptions: boolean;
}>) {
  const { username, hasAtLeastRole } = useAuth();

  const handleDeploymentChange = async (
    state: boolean,
    option: keyof Persona,
  ) => {
    const updatedData = { ...personaData, [option]: state };

    void onSave(updatedData, documents);
  };

  return (
    <>
      <div className="w-full border-none bg-darkGray rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col md:flex-row gap-4 md:gap-[100px] items-start md:items-center justify-between">
          <div className="flex flex-col w-full md:w-auto">
            <EditablePersonaHeader
              name={personaData.name ?? ""}
              imageSrc={`${apiUrl}/get-file?url=persona-knowledge-base/${
                personaData.persona_id === ""
                  ? username
                  : personaData.persona_id
              }/${personaData.avatar_id}`}
              videoSrc={personaData.persona_video_avatar}
              onAvatarChange={(e) => onFileChange(personaData.persona_id, e)}
              gender={personaData.gender || "male"}
            />
            {showHeaderOptions ? (
              <PersonaKnowledgeBaseStatus
                className="mt-3 text-center"
                status={documents.knowledge_graph_status}
                lastBuilt={documents?.last_built ?? undefined}
              />
            ) : null}
          </div>

          {showHeaderOptions ? (
            <div className="mt-2 flex flex-col justify-center gap-y-4 w-full md:w-auto">
              {hasAtLeastRole(Role.SuperAdmin) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="public_persona"
                    name="public_persona"
                    checked={personaData.public_persona || false}
                    onCheckedChange={(state) =>
                      handleDeploymentChange(state === true, "public_persona")
                    }
                  />
                  <label
                    htmlFor="public_persona"
                    className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Public Agent
                  </label>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enable_image_generation"
                  name="enable_image_generation"
                  checked={personaData.enable_image_generation || false}
                  onCheckedChange={(state) =>
                    handleDeploymentChange(
                      state === true,
                      "enable_image_generation",
                    )
                  }
                />
                <label
                  htmlFor="enable_image_generation"
                  className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Image Generation
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enable_tweet_generation"
                  name="enable_tweet_generation"
                  checked={personaData.enable_twitter_engagement || false}
                  onCheckedChange={(state) =>
                    handleDeploymentChange(
                      state === true,
                      "enable_twitter_engagement",
                    )
                  }
                />
                <label
                  htmlFor="enable_tweet_generation"
                  className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Tweet Generation
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <TwitterConnectionStatus
                  persona={personaData}
                  twitterHandle={personaData.twitter}
                  isConnected={Boolean(personaData?.twitter)}
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-4 mt-3 w-full">
            {hasAtLeastRole(Role.SuperAdmin) && (
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="tenant">Tenant</Label>
                <Input
                  type="text"
                  id="tenant"
                  value={personaData.qstarlabs_tenant || ""}
                  disabled
                />
              </div>
            )}

            {hasAtLeastRole(Role.TenantAdmin) && (
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="owner">Owner</Label>
                <Input
                  type="text"
                  id="owner"
                  value={personaData.persona_owner || ""}
                  disabled
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {children}
    </>
  );
}
